<template>
  <section class="cn-error-section">
    <div class="container">
      <div class="cn-error-section__content">
        <h1 class="cn-error-section__code">
          <span v-if="error.statusCode === 404">404</span>
          <span v-else>500</span>
        </h1>
        <p class="cn-error-section__error">
          <span v-if="error.statusCode === 404">
            {{ $t('page_not_found') }}</span
          >
          <span v-else>Server error</span>
        </p>
        <p class="cn-error-section__description">
          {{ $t('something_went_wrong') }}
        </p>
        <div class="cn-error-section__controls">
          <UiButton variant="red" to="/">{{ $t('home_page') }}</UiButton>
        </div>
        <h2 class="cn-error-section__categories">
          {{ $t('popular_categories') }}
        </h2>
        <ul class="cn-error-section__categories-list">
          <template v-for="(item, i) in menu">
            <li
              v-if="item.url"
              :key="i"
              class="cn-error-section__categories-item"
            >
              <NuxtLink
                :to="localePath(item.url)"
                class="cn-error-section__category"
              >
                <CdnImg
                  v-if="item.image"
                  provider="imgproxy"
                  :src="item.image.path"
                  :width="28"
                  :height="28"
                  format="webp"
                />
                <span>
                  {{ item.name }}
                </span>
              </NuxtLink>
            </li>
          </template>
        </ul>
        <div class="cn-error-section__parallelograms">
          <UiParallelogram
            class="cn-error-section__parallelogram-1"
            color="aqua"
            :style="{
              left: '-230px',
              top: '-120px',
              height: '100px',
              width: '60px',
            }"
          />
          <UiParallelogram
            class="cn-error-section__parallelogram-2"
            color="aqua"
            :style="{
              left: '-180px',
              top: '-260px',
              height: '50px',
              width: '30px',
            }"
          />
          <UiParallelogram
            color="aqua"
            :style="{
              left: '-520px',
              top: '240px',
              height: '30px',
              width: '20px',
            }"
          />
          <UiParallelogram
            class="cn-error-section__parallelogram-4"
            color="aqua"
            :style="{
              left: '160px',
              top: '-290px',
              height: '100px',
              width: '60px',
            }"
          />
          <UiParallelogram
            class="cn-error-section__parallelogram-5"
            color="aqua"
            :style="{
              left: '180px',
              top: '-140px',
              height: '30px',
              width: '20px',
            }"
          />
          <UiParallelogram
            color="aqua"
            :style="{
              left: '500px',
              top: '250px',
              height: '90px',
              width: '50px',
            }"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import { useMenus } from '@/composables';
import type { NuxtError } from '#app';
import UiButton from '@/components/ui/UiButton';
import UiParallelogram from '@/components/ui/UiParallelogram';

interface Props {
  error: NuxtError;
}

defineProps<Props>();

const localePath = useLocalePath();
const { menuBody: menu } = useMenus();
</script>

<style lang="scss">
.cn-error-section {
  --ab-canyon-error-section-code-color-text: var(--ab-canyon-color-primary);
  --ab-canyon-error-section-description-color-text: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-error-section-categories-color-text: var(
    --ab-canyon-color-primary
  );
  --ab-canyon-error-section-category-color-text-hover: var(
    --ab-canyon-color-accent
  );
  &__content {
    padding-top: var(--size-5xl);
    padding-bottom: var(--size-5xl);
    align-items: center;
    flex-direction: column;
    display: flex;
    position: relative;
    @media (min-width: 656px) {
      padding-top: calc(6 * var(--size-base));
      padding-bottom: calc(6 * var(--size-base));
      margin-bottom: var(--size-7xl);
    }
  }
  &__code {
    color: var(--ab-canyon-error-section-code-color-text);

    line-height: 1;
    font-size: 120px;
    @media (min-width: 656px) {
      font-size: 240px;
    }
  }
  &__error {
    color: var(--ab-canyon-error-section-code-color-text);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.75rem;
    margin-top: calc(var(--size-base) / 2);
    @media (min-width: 656px) {
      text-transform: uppercase;
      font-size: 1.875rem;
      line-height: 2.25rem;
    }
  }
  &__description {
    color: var(--ab-canyon-error-section-description-color-text);
    text-align: center;
    max-width: 250px;
    margin-top: var(--size-xl);
    @media (min-width: 656px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
      max-width: none;
    }
  }
  &__controls {
    margin-top: var(--size-4xl);
  }
  &__categories {
    color: var(--ab-canyon-error-section-categories-color-text);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.75rem;
    text-align: center;
    margin-top: var(--size-5xl);
    margin-left: auto;
    margin-right: auto;
  }
  &__categories-list {
    column-gap: var(--size-3xl);
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    display: flex;
    margin-top: var(--size-xs);
  }
  &__categories-item {
    font-size: 1.125rem;
    line-height: 1.75rem;
    padding: calc(var(--size-base) / 2);
  }
  &__category {
    transition-duration: 0.15s;
    transition-property: color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    align-items: center;
    display: flex;
    &:hover {
      color: var(--ab-canyon-error-section-category-color-text-hover);
    }

    img {
      margin-right: calc(var(--size-base) / 2);
    }
  }
  &__parallelograms {
    top: 50%;
    left: 50%;
    position: absolute;
    .cn-parallelogram {
      position: absolute;
      border-width: 2px;
      &.cn-error-section {
        &__parallelogram {
          &-1 {
            @media (min-width: 656px) {
              border-width: 3px;
              top: -150px !important;
              left: -620px !important;
            }
          }
          &-2 {
            @media (min-width: 656px) {
              top: -60px !important;
              left: -720px !important;
            }
          }
          &-4 {
            @media (min-width: 656px) {
              border-width: 3px;
              top: -200px !important;
              left: 500px !important;
            }
          }
          &-5 {
            @media (min-width: 656px) {
              top: 50px !important;
              left: 700px !important;
            }
          }
        }
      }
    }
  }
}
</style>
